import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import type { ClinicalNote, Episode, TreatmentPlan } from '../pages/clients/data/types';
import { treatmentPlanFromCarePlan, treatmentPlanFromQuestionnaireResponse } from '../lib/treatmentPlanFromFhir';
import {
  TEMPLATE_CATEGORY_SYSTEM,
  TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  getCarePlanEpisodeId,
  getCarePlanQuestionnaireResponseId,
  isTreatmentPlanCarePlan,
  pickCurrentSignedTreatmentPlanCarePlan,
} from '@unify/fhir-model/resources/treatment-plan-careplan';
import type { CarePlan } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';

function noteTitle(note: ClinicalNote): string {
  return (note.title ?? note.serviceType ?? note.noteType ?? '').toLowerCase();
}

/** IOP supplemental Meridian Continuing Recovery Plan chart document. */
export function isIopContinuingRecoveryChartNote(note: ClinicalNote): boolean {
  return noteTitle(note).includes('continuing recovery');
}

/** Primary Diagnosis & Treatment Plan chart document (all modalities). */
export function isPrimaryTreatmentPlanChartNote(note: ClinicalNote): boolean {
  if (isIopContinuingRecoveryChartNote(note)) return false;
  const title = noteTitle(note);
  return title.includes('treatment plan') || title.includes('diagnosis and treatment');
}

/** @deprecated Prefer isPrimaryTreatmentPlanChartNote or isIopContinuingRecoveryChartNote */
export function isTreatmentPlanChartNote(note: ClinicalNote): boolean {
  return isPrimaryTreatmentPlanChartNote(note) || isIopContinuingRecoveryChartNote(note);
}

export function treatmentPlanBaseFromEpisode(episode: Episode): TreatmentPlan {
  const status: TreatmentPlan['status'] =
    episode.status === 'finished'
      ? 'completed'
      : episode.status === 'active' || episode.status === 'follow-up'
        ? 'active'
        : 'on-hold';

  return {
    id: `tp-${episode.id}`,
    program: episode.name,
    provider: episode.provider,
    startDate: episode.startDate,
    status,
    goals: [],
  };
}

export interface ChartTreatmentPlanEntry {
  chartDocumentId: string;
  episode: Episode;
  plan: TreatmentPlan | undefined;
  /** Set for IOP continuing recovery entries; used for display labels. */
  documentTitle?: string;
}

export type TreatmentPlanChartDocumentKind = 'primary' | 'iop-continuing-recovery';

function matchesChartPlanKind(note: ClinicalNote, kind: TreatmentPlanChartDocumentKind): boolean {
  return kind === 'iop-continuing-recovery'
    ? isIopContinuingRecoveryChartNote(note)
    : isPrimaryTreatmentPlanChartNote(note);
}

/**
 * Most recent chart document of the given kind per episode (single episode when episodeId is set).
 */
export function findTreatmentPlanChartNotesByEpisode(
  clinicalNotes: ClinicalNote[],
  episodes: Episode[],
  episodeId?: string,
  kind: TreatmentPlanChartDocumentKind = 'primary',
): Array<{ episode: Episode; note: ClinicalNote }> {
  const episodeById = new Map(episodes.map((episode) => [episode.id, episode]));
  const episodeIds = episodeId
    ? [episodeId]
    : [...new Set(clinicalNotes.map((note) => note.episodeId).filter(Boolean) as string[])];

  const results: Array<{ episode: Episode; note: ClinicalNote }> = [];

  for (const eid of episodeIds) {
    const episode = episodeById.get(eid);
    if (!episode) continue;

    const candidates = clinicalNotes
      .filter(
        (clinicalNote) =>
          clinicalNote.episodeId === eid
          && clinicalNote.chartDocumentResponseId
          && matchesChartPlanKind(clinicalNote, kind),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
    const note = candidates.find((candidate) => candidate.noteStatus === 'Signed')
      ?? candidates[0];

    if (note?.chartDocumentResponseId) {
      results.push({ episode, note });
    }
  }

  return results.sort((a, b) => b.note.date.localeCompare(a.note.date));
}

function carePlanRecency(plan: CarePlan): string {
  return plan.meta?.lastUpdated ?? plan.created ?? '';
}

/** Active signed plan for the episode, otherwise the most recently updated CarePlan. */
export function selectEpisodeTreatmentPlanCarePlan(
  plans: CarePlan[],
  episodeId: string,
): CarePlan | undefined {
  const episodePlans = plans.filter(
    (plan) =>
      isTreatmentPlanCarePlan(plan)
      && getCarePlanEpisodeId(plan) === episodeId
      && plan.status !== 'entered-in-error'
      && plan.status !== 'revoked',
  );
  return (
    pickCurrentSignedTreatmentPlanCarePlan(episodePlans, episodeId)
    ?? [...episodePlans].sort((a, b) => carePlanRecency(b).localeCompare(carePlanRecency(a)))[0]
  );
}

async function treatmentPlanViewFromCarePlan(
  medplum: MedplumClient,
  carePlan: CarePlan,
  base: TreatmentPlan,
): Promise<TreatmentPlan> {
  const goalIds = (carePlan.goal ?? [])
    .map((goal) => goal.reference?.replace(/^Goal\//, ''))
    .filter((id): id is string => typeof id === 'string' && !id.startsWith('urn:'));
  if (goalIds.length !== (carePlan.goal ?? []).length) {
    throw new Error('CarePlan contains unresolved Goal references');
  }
  const goals = await Promise.all(goalIds.map((id) => medplum.readResource('Goal', id)));
  const conditionIds = (carePlan.addresses ?? [])
    .map((address) => address.reference?.replace(/^Condition\//, ''))
    .filter((id): id is string => Boolean(id));
  const conditions = await Promise.all(
    conditionIds.map((id) => medplum.readResource('Condition', id)),
  );
  return treatmentPlanFromCarePlan(carePlan, goals, conditions, base);
}

/**
 * Current treatment plan for an episode.
 * Prefers the active CarePlan so a newer unsigned draft does not hide the signed plan.
 * QuestionnaireResponse is used only when the episode has no CarePlan yet.
 */
export async function loadEpisodeTreatmentPlanView(
  medplum: MedplumClient,
  patientId: string,
  episode: Episode,
  clinicalNotes: ClinicalNote[] = [],
): Promise<TreatmentPlan | undefined> {
  const base = treatmentPlanBaseFromEpisode(episode);
  let plans: CarePlan[] = [];
  try {
    plans = await medplum.searchResources('CarePlan', {
      subject: `Patient/${patientId}`,
      _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
      _count: '50',
    });
  } catch {
    plans = [];
  }
  const selected = selectEpisodeTreatmentPlanCarePlan(plans, episode.id);
  if (selected) {
    try {
      return await treatmentPlanViewFromCarePlan(medplum, selected, base);
    } catch {
      return undefined;
    }
  }

  const note = findTreatmentPlanChartNotesByEpisode(clinicalNotes, [episode], episode.id, 'primary')[0]?.note;
  const responseId = note?.chartDocumentResponseId;
  if (!responseId) return undefined;
  const qr = await medplum.readResource('QuestionnaireResponse', responseId);
  return treatmentPlanFromQuestionnaireResponse(qr, base);
}

export async function loadPrimaryTreatmentPlanView(
  medplum: MedplumClient,
  responseId: string,
  base: TreatmentPlan,
): Promise<TreatmentPlan | undefined> {
  const qr = await medplum.readResource('QuestionnaireResponse', responseId);
  try {
    const plans = await medplum.searchResources('CarePlan', {
      identifier: `${TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM}|${responseId}`,
      _count: '1',
    });
    const carePlan = plans[0] as CarePlan | undefined;
    if (carePlan && (carePlan.status === 'active' || carePlan.status === 'completed')) {
      return await treatmentPlanViewFromCarePlan(medplum, carePlan, base);
    }
  } catch {
    /* QR fallback */
  }
  return treatmentPlanFromQuestionnaireResponse(qr, base);
}

export function useChartTreatmentPlans(
  clientId: string | undefined,
  episodes: Episode[],
  clinicalNotes: ClinicalNote[],
  episodeId?: string,
  kind: TreatmentPlanChartDocumentKind = 'primary',
): { entries: ChartTreatmentPlanEntry[]; loading: boolean } {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<ChartTreatmentPlanEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const noteTargets = useMemo(
    () => findTreatmentPlanChartNotesByEpisode(clinicalNotes, episodes, episodeId, kind),
    [clinicalNotes, episodes, episodeId, kind],
  );

  const targetEpisodes = useMemo(() => {
    if (episodeId) return episodes.filter((episode) => episode.id === episodeId);
    return episodes;
  }, [episodes, episodeId]);

  useEffect(() => {
    if (!clientId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    if (kind === 'primary') {
      if (targetEpisodes.length === 0) {
        setEntries([]);
        setLoading(false);
        return;
      }

      let cancelled = false;
      setLoading(true);
      Promise.all(
        targetEpisodes.map(async (episode) => {
          try {
            const plan = await loadEpisodeTreatmentPlanView(
              medplum,
              clientId,
              episode,
              clinicalNotes,
            );
            return {
              chartDocumentId: plan?.id ?? episode.id,
              episode,
              plan,
            };
          } catch {
            return { chartDocumentId: episode.id, episode, plan: undefined };
          }
        }),
      )
        .then((loaded) => {
          if (!cancelled) setEntries(loaded.filter((entry) => entry.plan));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }

    if (noteTargets.length === 0) {
      setEntries([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all(
      noteTargets.map(async ({ episode, note }) => {
        const responseId = note.chartDocumentResponseId!;
        const documentTitle = note.title ?? note.serviceType;
        return { chartDocumentId: responseId, episode, plan: undefined, documentTitle };
      }),
    )
      .then((loaded) => {
        if (!cancelled) setEntries(loaded);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, clinicalNotes, kind, medplum, noteTargets, targetEpisodes]);

  return { entries, loading };
}

const NO_EPISODES: Episode[] = [];
const NO_CLINICAL_NOTES: ClinicalNote[] = [];

/**
 * CarePlan-derived chart-document ids, filled in from each episode's latest
 * treatment-plan chart document for episodes the CarePlan search did not cover.
 */
export function mergeTreatmentPlanChartDocumentIds(
  carePlanChartDocumentIds: ReadonlyMap<string, string>,
  episodes: Episode[],
  clinicalNotes: ClinicalNote[],
): Map<string, string> {
  const byEpisode = new Map<string, string>();
  for (const { episode, note } of findTreatmentPlanChartNotesByEpisode(clinicalNotes, episodes)) {
    byEpisode.set(episode.id, note.chartDocumentResponseId!);
  }
  for (const [episodeId, responseId] of carePlanChartDocumentIds) {
    byEpisode.set(episodeId, responseId);
  }
  return byEpisode;
}

/**
 * Chart-document id for the current CarePlan on each episode, when the plan still has a signature document.
 *
 * Roles whose policy omits CarePlan (admin, assistant) fall back to the episode's
 * latest treatment-plan chart document, so the episode card still links to the
 * existing plan instead of offering to create a duplicate one.
 */
export function useTreatmentPlanChartDocumentIds(
  patientId: string | undefined,
  episodes: Episode[] = NO_EPISODES,
  clinicalNotes: ClinicalNote[] = NO_CLINICAL_NOTES,
): Map<string, string> {
  const medplum = useMedplum();
  const [ids, setIds] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    if (!patientId) {
      setIds(new Map());
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const plans = await medplum.searchResources('CarePlan', {
          subject: `Patient/${patientId}`,
          _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
          _count: '100',
        });
        const byEpisode = new Map<string, CarePlan[]>();
        for (const plan of plans) {
          if (!isTreatmentPlanCarePlan(plan)) continue;
          const episodeId = getCarePlanEpisodeId(plan);
          if (!episodeId) continue;
          const list = byEpisode.get(episodeId) ?? [];
          list.push(plan);
          byEpisode.set(episodeId, list);
        }
        const next = new Map<string, string>();
        for (const [episodeId, episodePlans] of byEpisode) {
          const selected = selectEpisodeTreatmentPlanCarePlan(episodePlans, episodeId);
          const responseId = selected ? getCarePlanQuestionnaireResponseId(selected) : undefined;
          if (responseId) next.set(episodeId, responseId);
        }
        if (!cancelled) setIds(next);
      } catch {
        if (!cancelled) setIds(new Map());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId]);

  return useMemo(
    () => mergeTreatmentPlanChartDocumentIds(ids, episodes, clinicalNotes),
    [clinicalNotes, episodes, ids],
  );
}

/** @deprecated Use useChartTreatmentPlans */
export function useChartEpisodeTreatmentPlan(
  clientId: string | undefined,
  episode: Episode | undefined,
  clinicalNotes: ClinicalNote[],
): { plan: TreatmentPlan | undefined; loading: boolean; chartDocumentId: string | undefined } {
  const { entries, loading } = useChartTreatmentPlans(
    clientId,
    episode ? [episode] : [],
    clinicalNotes,
    episode?.id,
    'primary',
  );
  const entry = entries[0];
  return {
    plan: entry?.plan,
    loading,
    chartDocumentId: entry?.chartDocumentId,
  };
}
